import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ArrowRight, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { REALISATIONS, REALISATION_FILTERS } from "../data/realisations";

const Realisations = () => {
  const [filter, setFilter] = useState("Toutes");
  const items = (filter === "Toutes" ? REALISATIONS : REALISATIONS.filter((r) => r.technique === filter))
    .slice()
    .sort((a, b) => Number(a.illustration) - Number(b.illustration));

  return (
    <>
      <Seo
        title="Réalisations — sols en résine, béton ciré, moquette de pierre | Resineko"
        description="Découvrez les univers Resineko : résine décorative, industrielle, pailletée, béton ciré, moquette de pierre et peinture. Galerie par technique."
        path="/realisations"
      />
      <PageHero
        kicker="Réalisations"
        title="Nos univers, en images"
        subtitle="Un aperçu des techniques et des ambiances que nous réalisons pour nos clients, partout en France."
        image="/images/hero-main.jpg"
        dim
      />

      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-start gap-3 border border-[#047857]/30 bg-[#047857]/5 p-5 text-xs leading-relaxed text-[#8C8C8C]" data-testid="illustration-notice">
              <Info size={16} className="mt-0.5 shrink-0 text-[#2FBF8F]" />
              Toutes les photographies présentées sur cette page sont de vrais chantiers réalisés par Resineko. Cliquez sur une réalisation pour découvrir le récit complet du chantier : lieu, surface et étapes des travaux. La galerie s'enrichit au fil de nos projets.
            </div>
          </Reveal>

          <div className="mt-12 flex flex-wrap gap-2.5" data-testid="realisations-filters">
            {REALISATION_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`border px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  filter === f
                    ? "border-[#047857] bg-[#047857] text-white"
                    : "border-white/15 text-[#8C8C8C] hover:border-[#047857]/60 hover:text-[#F4F4F0]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {items.map((r) => {
                const content = (
                  <>
                    <div className="relative overflow-hidden">
                      <img
                        src={r.image}
                        alt={`${r.technique} — ${r.espace}${r.ville ? ` à ${r.ville}` : ""} — Réalisation Resineko`}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <span className={`absolute left-4 top-4 border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] backdrop-blur-sm ${
                        r.illustration
                          ? "border-white/20 bg-black/50 text-[#F4F4F0]/80"
                          : "border-[#2FBF8F]/50 bg-[#047857]/60 text-white"
                      }`}>
                        {r.illustration ? "Visuel d'illustration" : "Réalisation Resineko"}
                      </span>
                      {r.ville && (
                        <span className="absolute bottom-4 left-4 flex items-center gap-1.5 border border-white/20 bg-black/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F4F4F0] backdrop-blur-sm">
                          <MapPin size={11} className="text-[#2FBF8F]" /> {r.ville}
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#2FBF8F]">{r.technique}</p>
                      <h3 className="mt-2 font-serif text-2xl font-light text-[#F4F4F0]">{r.espace}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-[#8C8C8C]">{r.description}</p>
                      {r.slug && (
                        <p className="mt-4 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#2FBF8F]">
                          Découvrir le chantier <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </p>
                      )}
                    </div>
                  </>
                );
                return (
                  <motion.article
                    key={r.id}
                    layout
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="group border border-white/10 bg-[#111] transition-colors duration-300 hover:border-[#047857]/60"
                    data-testid={`realisation-card-${r.id}`}
                  >
                    {r.slug ? (
                      <Link to={`/realisations/${r.slug}`} data-testid={`realisation-link-${r.slug}`} className="block">
                        {content}
                      </Link>
                    ) : content}
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <CTASection
        title="Votre projet mérite sa place ici."
        text="Parlez-nous de votre espace : nous vous orientons vers la technique et la finition adaptées."
      />
    </>
  );
};

export default Realisations;
